import { useEffect, useRef, useState } from "react";
import {
  createDashboardFetchCapture,
  fetchDashboardCriticalShell,
  fetchDashboardDeferredBackground,
  fetchDashboardPriorityBackground,
  fetchLedgerSummary,
  mergeDashboardErrors,
} from "./api";
import type { DashboardData, DashboardState } from "./types";

// entryObservability excluded from cache — see useTradeCycleProgress (4s poll)

const REFRESH_MS = 8_000;
const CACHE_KEY = "hypermax-dashboard-cache";

/** Bump when dashboard cache semantics change (e.g. fixed-cycle observability window). */
export const CACHE_VERSION = 8;
export const CACHE_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes (much safer for trading view)

function dataForCache(data: DashboardData): DashboardData {
  const { entryObservability: _cycle, ...rest } = data;
  return rest;
}

export interface CachedDashboardState {
  _v: number;
  refreshedAt: string;
  data: DashboardData;
}

interface NavigatorConnection {
  effectiveType?: string;
  saveData?: boolean;
}

function getRefreshMs() {
  const conn = (navigator as Navigator & { connection?: NavigatorConnection }).connection;
  if (conn?.effectiveType === "2g" || conn?.saveData) return 15_000;
  if (conn?.effectiveType === "3g" || conn?.effectiveType === "slow-2g") return 10_000;
  return REFRESH_MS;
}

export function loadCachedState(): DashboardState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // Minimal shape guard + version + age. Never blindly trust full DashboardState from storage.
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed as Record<string, unknown>)._v !== CACHE_VERSION ||
      typeof (parsed as Record<string, unknown>).refreshedAt !== "string" ||
      typeof (parsed as Record<string, unknown>).data !== "object" ||
      (parsed as Record<string, unknown>).data === null
    ) {
      return null;
    }
    const cached = parsed as CachedDashboardState;
    const ts = Date.parse(cached.refreshedAt);
    if (!Number.isFinite(ts)) return null;
    const age = Date.now() - ts;
    if (age > CACHE_MAX_AGE_MS || age < 0) return null;
    return {
      data: cached.data,
      loading: false,
      refreshedAt: cached.refreshedAt,
    };
  } catch {
    return null;
  }
}

export function mergeDashboardPhase(
  prev: DashboardData,
  phase: Partial<DashboardData>,
  phaseErrors?: DashboardData["errors"],
): DashboardData {
  const failed = new Set([
    ...Object.keys(phaseErrors?.critical ?? {}),
    ...Object.keys(phaseErrors?.background ?? {}),
  ]);
  const next: DashboardData = { ...prev };
  for (const [key, value] of Object.entries(phase)) {
    if (key === "errors" || value === undefined || failed.has(key)) continue;
    (next as Record<string, unknown>)[key] = value;
  }
  if (phase.errors) {
    next.errors = mergeDashboardErrors(prev.errors, phase.errors);
  }
  return next;
}

export function saveCache(state: DashboardState) {
  try {
    // 1.6 hardening: never persist states that carry a critical error (state.error gate; aligns with plan's "!criticalError save gate" — covers partial criticalError from missing core data and network fallbacks)
    if (
      !state.refreshedAt ||
      typeof state.data !== "object" ||
      state.error ||
      !state.data.health ||
      !state.data.portfolio
    ) {
      return;
    }
    const toCache: CachedDashboardState = {
      _v: CACHE_VERSION,
      refreshedAt: state.refreshedAt,
      data: dataForCache(state.data),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(toCache));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function useDashboardData(): DashboardState & { fromCache: boolean } {
  const cached = useRef(loadCachedState());
  const fromCacheRef = useRef<boolean>(!!cached.current);

  const [state, setState] = useState<DashboardState & { fromCache: boolean }>(
    cached.current
      ? { ...cached.current, fromCache: true }
      : { data: {}, loading: true, fromCache: false },
  );
  const lastGood = useRef<DashboardState["data"]>(cached.current?.data ?? {});
  const refreshMs = useRef(getRefreshMs());

  // Persist latest successful (non-cached, non-error) fetch to localStorage.
  // Never persist partial error states or network-error fallbacks (state.error present; this is the !criticalError-equivalent save gate).
  // Cache-loaded initial state may trigger a no-op re-save of identical slim data.
  useEffect(() => {
    if (!state.loading && state.refreshedAt && !state.error) {
      saveCache(state);
    }
  }, [state.data, state.error, state.refreshedAt, state.loading]);

  // Re-evaluate refresh interval on mount in case connection type changes
  useEffect(() => {
    refreshMs.current = getRefreshMs();
  }, []);

  useEffect(() => {
    let mounted = true;
    let timer: number | undefined;
    let controller: AbortController | undefined;
    let pending = false;

    async function refresh() {
      // Skip refresh when tab is hidden to save battery / data
      if (document.hidden) {
        if (mounted) timer = window.setTimeout(refresh, refreshMs.current);
        return;
      }
      if (pending) return;
      pending = true;
      controller?.abort();
      controller = new AbortController();
      try {
        const capture = createDashboardFetchCapture();
        const ledgerPromise = capture.captureCritical(
          "ledger",
          fetchLedgerSummary(controller.signal),
        );
        const shell = await fetchDashboardCriticalShell(controller.signal, capture);
        if (!mounted || controller.signal.aborted) return;
        lastGood.current = mergeDashboardPhase(
          {
            ...lastGood.current,
            entryObservability: lastGood.current.entryObservability,
          },
          shell,
          shell.errors,
        );
        fromCacheRef.current = false;
        const criticalError =
          !shell.health || !shell.portfolio
            ? (Object.values(shell.errors?.critical ?? {})[0] ?? "연결 상태를 확인하고 있습니다.")
            : undefined;
        setState({
          data: lastGood.current,
          loading: false,
          error: criticalError,
          refreshedAt: new Date().toISOString(),
          fromCache: false,
        });

        const ledger = await ledgerPromise;
        if (!mounted || controller.signal.aborted) return;
        const shellAndLedgerErrors = capture.finalizeErrors();
        lastGood.current = mergeDashboardPhase(
          {
            ...lastGood.current,
            entryObservability: lastGood.current.entryObservability,
          },
          { ledger, errors: shellAndLedgerErrors },
          shellAndLedgerErrors,
        );
        setState({
          data: lastGood.current,
          loading: false,
          error: criticalError,
          refreshedAt: new Date().toISOString(),
          fromCache: false,
        });

        const priority = await fetchDashboardPriorityBackground(controller.signal, capture);
        if (!mounted || controller.signal.aborted) return;
        const priorityErrors = mergeDashboardErrors(shellAndLedgerErrors, priority.errors);
        lastGood.current = mergeDashboardPhase(
          {
            ...lastGood.current,
            entryObservability: lastGood.current.entryObservability,
          },
          { ...priority, errors: priorityErrors },
          priority.errors,
        );
        setState({
          data: lastGood.current,
          loading: false,
          error: criticalError,
          refreshedAt: new Date().toISOString(),
          fromCache: false,
        });

        const deferred = await fetchDashboardDeferredBackground(controller.signal, capture);
        if (!mounted || controller.signal.aborted) return;
        const deferredErrors = mergeDashboardErrors(priorityErrors, deferred.errors);
        lastGood.current = mergeDashboardPhase(
          {
            ...lastGood.current,
            entryObservability: lastGood.current.entryObservability,
          },
          { ...deferred, errors: deferredErrors },
          deferred.errors,
        );
        setState({
          data: lastGood.current,
          loading: false,
          error: criticalError,
          refreshedAt: new Date().toISOString(),
          fromCache: false,
        });
      } catch (error) {
        if (!mounted || controller.signal.aborted) return;
        // On fallback to lastGood (which may be from initial cache), preserve the fromCache flag
        // so UI can indicate the displayed data is not from a live fetch.
        setState({
          data: lastGood.current,
          loading: false,
          error: error instanceof Error ? error.message : "연결 상태를 확인하고 있습니다.",
          refreshedAt: new Date().toISOString(),
          fromCache: fromCacheRef.current,
        });
      } finally {
        pending = false;
        if (mounted) timer = window.setTimeout(refresh, refreshMs.current);
      }
    }

    void refresh();

    function onVisibilityChange() {
      if (!document.hidden && mounted) {
        window.clearTimeout(timer);
        void refresh();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      mounted = false;
      controller?.abort();
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return state;
}
